<template>
  <pipeline-layout is-column-style>
    <!--Menu-->
    <pipeline-layout-menu>
      <tabs-horizontal
        :value="currentTab"
        @input="changedTab"
      >
        <b-tab-item value="manual">
          <template #header>
            <span>
              Manual
              <badge-default
                v-if="manualPastDueCount"
                :count="manualPastDueCount"
              ></badge-default>
            </span>
          </template>

          <section-box has-no-padding>
            <template v-if="records.order.length">
              <lazy-render
                v-for="recordId in records.order"
                :key="recordId"
                :min-height="121"
                :unrender="true"
              >
                <follow-up-item
                  :record="records.items[recordId]"
                  :is-active="recordId === activeItemIndex"
                  @click="selectRecord(recordId)"
                  is-manual
                ></follow-up-item>
              </lazy-render>
            </template>
            <div v-else class="follow_up_menu--empty">
              <h4 class="follow_up_menu--empty--heading">No Manual Follow Ups</h4>
              <p class="follow_up_menu--empty--message">Any leads you have place in follow up statuses and not in
                automated follow up sequence will show up here.</p>
            </div>
          </section-box>

          <div v-if="!loadingMoreRecords && hasMoreRecordsToLoad" class="flex flex-centered my-200">
            <button-outlined size="" @click="loadMoreRecords">Load More Records</button-outlined>
          </div>
        </b-tab-item>

        <b-tab-item value="automated" label="Automated">
          <section-box v-if="hasFollowUpSequenceFeature" has-no-padding>
            <template v-if="records.order.length">
              <lazy-render
                v-for="recordId in records.order"
                :key="recordId"
                :min-height="142"
                :unrender="true"
              >
                <follow-up-item
                  :record="records.items[recordId]"
                  :is-active="recordId === activeItemIndex"
                  @click="activeItemIndex = recordId"
                  is-automated
                ></follow-up-item>
              </lazy-render>
            </template>
            <div v-else class="follow_up_menu--empty">
              <h4 class="follow_up_menu--empty--heading">No Automated Follow Ups</h4>
              <p class="follow_up_menu--empty--message">Any leads placed in automated follow up sequence with follow up statuses will show up here.</p>
            </div>
          </section-box>

          <section-box v-else has-no-padding>
            <div class="upgrade_message">
              <h4 class="upgrade_message--heading">Upgrade Required</h4>

              <p class="upgrade_message--content"><a @click="openBillingPortal">Upgrade your account today</a> and gain
                access to the complete automated follow up sequences that you can copy & paste into your business today!</p>
              <p class="upgrade_message--content">Cross channel, automated follow up sequences from ForeFront are
              always working for you!  It's time to let the system do the heavy lifting when it comes to nurturing your leads.
              Automated drip sequences can transform your business without spending more on marketing.</p>
              <p class="upgrade_message--content">You're ready to take the next step and start closing more deals with
              less effort!</p>

              <div class="flex flex-centered">
                <button-outlined
                  class="upgrade_message--button"
                  @click="openBillingPortal"
                  size="">Upgrade Your Account Now
                </button-outlined>
              </div>
            </div>
          </section-box>

          <div v-if="!loadingMoreRecords && hasMoreRecordsToLoad" class="flex flex-centered my-200">
            <button-outlined size="" @click="loadMoreRecords">Load More Records</button-outlined>
          </div>
        </b-tab-item>
      </tabs-horizontal>
    </pipeline-layout-menu>

    <!--Main Content-->
    <template v-if="activeItemIndex">
      <template
        v-for="recordId in records.order"
      >
        <follow-up-content
          v-if="recordId === activeItemIndex"
          :key="recordId"
          :contact="records.items[recordId].contact"
          :record="records.items[recordId]"
        ></follow-up-content>
      </template>
      <app-loading-section :loading="pageLoading"></app-loading-section>
    </template>
    <div v-else class="follow_up_content_empty">
      <div class="follow_up_content_empty--box">
        <p class="follow_up_content_empty--heading">Choose an item in the menu to the left</p>
        <p class="follow_up_content_empty--message">This is where the leads you are following up will reside. You can
          control the lead statuses that show here in the
          <router-link :to="{name: 'settings', params: {tab: 'pipeline'}}">Pipeline Settings</router-link>
        </p>
      </div>
    </div>

    <!--Filters-->
    <template #filters>
      <p class="record_count flex flex-vertical-center">
        <span><strong>{{ records.order.length }}</strong> leads</span>
      </p>

      <follow-up-filters></follow-up-filters>
    </template>
  </pipeline-layout>
</template>

<script>
import FollowUpItem from "./components/FollowUpItem";
import PipelineLayout from "../../components/PipelineLayout";
import PipelineLayoutMenu from "../../components/PipelineLayoutMenu";
import FollowUpContent from "./components/FollowUpContent";
import FollowUpFilters from "./components/FollowUpFilters";
import {EntityCollection} from "../../../../server/EntityCollection.class";
import LazyRender from "../../../../components/LazyRender";

export default {
  name: "FollowUpPipelineDesktop",
  components: {
    LazyRender,
    FollowUpFilters,
    FollowUpContent,
    PipelineLayoutMenu,
    PipelineLayout,
    FollowUpItem,
  },
  data() {
    return {
      pageLoading: true,
      activeItemIndex: 0,
      loadingMoreRecords: false,
      stopWatcherOnLoad: true
    };
  },
  watch: {
    // watches if user changes the value of Pipeline View option
    'pipelineViewId'() {
      if (!this.stopWatcherOnLoad) {
        this.changedTab(this.currentTab)
      }
    },
  },
  computed: {
    pipelineViewId() {
      return this.$store.getters["activeState/pipelineViewId"];
    },
    currentTab() {
      return this.$store.getters["followUpState/activeTab"];
    },
    hasMoreRecordsToLoad() {
      return this.$store.getters["followUpState/hasMoreRecordsToLoad"];
    },
    hasFollowUpSequenceFeature() {
      return this.$store.getters.account.has_follow_up_sequence_feature;
    },
    records() {
      if (this.currentTab === "automated") {
        return this.automatedFollowUps;
      }

      if (this.currentTab === "manual") {
        return this.manualFollowUps;
      }

      return new EntityCollection();
    },
    manualFollowUps() {
      return this.$store.getters["recordsInManualFollowUp/records"];
    },
    automatedFollowUps() {
      return this.$store.getters["recordsInAutomatedFollowUp/records"];
    },
    manualPastDueCount() {
      const now = new Date();

      const manualPastDue = this.manualFollowUps.order.filter(followUpId => {
        const followUp = this.manualFollowUps.items[followUpId];

        if (!followUp.next_contact_at) return false;

        const nextContactAt = new Date(followUp.next_contact_at);
        return nextContactAt.getTime() <= now.getTime();
      });

      return manualPastDue.length;
    }
  },
  created() {
    this.stopWatcherOnLoad = true
    this.changedTab("manual").then(() => {
      this.pageLoading = false
      this.stopWatcherOnLoad = false
    });
  },
  destroyed() {
    this.$store.dispatch("followUpState/tearDown");
  },
  methods: {
    selectRecord(recordId) {
      this.activeItemIndex = recordId;
    },
    changedTab(newTab) {
      this.activeItemIndex = null;
      this.pageLoading = true;

      return this.$store.dispatch("followUpState/activeTab", newTab).then(() => {
        this.pageLoading = false;

        if (newTab === "manual") {
          if (this.manualFollowUps.order.length) {
            this.activeItemIndex = this.manualFollowUps.items[this.manualFollowUps.order[0]];
          }
        }

        if (newTab === "automated") {
          if (this.automatedFollowUps.order.length) {
            this.activeItemIndex = this.automatedFollowUps.items[this.automatedFollowUps.order[0]];
          }
        }
      });
    },
    loadMoreRecords() {
      this.loadingMoreRecords = true;

      if (this.currentTab === "manual") {
        return this.$store.dispatch("recordsInManualFollowUp/loadMoreRecords")
          .finally(() => {
            this.loadingMoreRecords = false;
          });
      }
      if (this.currentTab === "automated") {
        return this.$store.dispatch("recordsInAutomatedFollowUp/loadMoreRecords")
          .finally(() => {
            this.loadingMoreRecords = false;
          });
      }
    },
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/variables/variables";

.record_count {
  margin-right: 12px;

  span {
    flex: none;
    padding: 2px 8px 2px 0;
    border-right: 2px solid $borderDark;
    color: $text-muted;
  }
}

.upgrade_message {
  padding: 2rem 20px;

  &--heading {
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
  }

  &--content {
    margin-bottom: 10px;
    line-height: 1.4;

    a {
      font-weight: bold;
    }
  }

  &--button {
    margin-top: 1.5rem;
  }
}

.follow_up_menu {
  &--empty {
    padding: 8px;
    line-height: 1.2;

    &--heading {
      margin-bottom: 8px;
    }
  }
}

.follow_up_content_empty {
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;

  &--box {
    margin-top: -4rem;
    padding: 4rem 6rem;
    border: 1px solid $borderDark;
    font-style: italic;
    text-align: center;
  }

  &--heading {
    margin-bottom: 12px;
    font-size: 2rem;
    color: $text-muted;
  }

  &--message {
    max-width: 700px;
    margin: 0 auto;
    font-size: 1.25rem;
    color: $text-light;
    line-height: 1.4;
  }
}
</style>
